<script setup lang="ts">

import {useDebounceFn, watchIgnorable} from "@vueuse/core";
import {onMounted, type Ref, ref, toRaw, watch} from "vue";
import type {StudentResponse} from "../../../../responses/Site/StudentResponse.ts";
import {label} from "../../../mixins/translations.ts";
import {useEnrollmentStore} from "../../../stores/enrollment.ts";
import FloatingInput from "../../../ui/inputs/FloatingInput.vue";

interface Props {
    modelValue: StudentResponse;
    errors: { [key: string]: string };
}

const props = withDefaults(defineProps<Props>(), {
    errors: {}
});

const store = useEnrollmentStore();
const emit = defineEmits(['update:modelValue']);


const student: Ref<StudentResponse> = ref({
    uuid: store.generateUuid()
});
//const errors: Ref<{ [key: string]: string }> = ref({});
const success: Ref<{ [key: string]: boolean }> = ref({});

const today = new Date();
today.setFullYear(today.getFullYear() - 16);
const minimumAge = today.toISOString().split('T')[0];
today.setFullYear(today.getFullYear() - 54);
const maximumAge = today.toISOString().split('T')[0];

const validateField = async (fieldName, formData) => {
    student.value[fieldName] = student.value[fieldName]?.trim();
    try {
        await store.studentValidation.validateAt(fieldName, formData);

        if(fieldName === 'insertion' && student.value[fieldName] === '') return delete success.value[fieldName];
        success.value[fieldName] = true;
        delete props.errors[fieldName];
    } catch (error) {
        props.errors[fieldName] = error.message;
        success.value[fieldName] = false;
    }
}

const {ignoreUpdates} = watchIgnorable(() => student.value, useDebounceFn((newValue) => {
    emit('update:modelValue', newValue)
}, 500), {deep: true})

onMounted(() => {
    ignoreUpdates(() => {
        student.value = props.modelValue;
        validate(true);
    })
});

const validate = (initial = false) => {
    Object.entries(student.value).forEach(([key, value]) => {
        if(!initial) {  //also check if empty
            validateField(key, student.value);
        } else {
            if (value) {
                validateField(key, student.value);
            }
        }
    });
}
defineExpose({ validate })

</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="grid gap-4 md:grid-cols-3">
            <FloatingInput v-model="student.initials" @focusout="validateField('initials', student)" @change="validateField('initials', student)" :error="errors.initials" :success="success.initials" :label="`${label('site.initials')} *`" />
            <FloatingInput v-model="student.insertion" @change="validateField('insertion', student)"  :error="errors.insertion" :success="success.insertion" :label="`${label('site.insertion')}`"  />
            <FloatingInput v-model="student.last_name" @focusout="validateField('last_name', student)" @change="validateField('last_name', student)" :error="errors.last_name" :success="success.last_name"  :label="`${label('site.last_name')} *`"  />
        </div>

        <div class="grid gap-4 md:grid-cols-2">
            <FloatingInput v-model="student.first_name" @focusout="validateField('first_name', student)" @change="validateField('first_name', student)" :error="errors.first_name" :success="success.first_name"  :label="`${label('site.first_name')} *`"  />
            <FloatingInput type="date" :max="minimumAge" :min="maximumAge" v-model="student.date_of_birth" @input="validateField('date_of_birth', student)" :error="errors.date_of_birth" :success="success.date_of_birth" :label="`${label('site.date_of_birth')} *`"  />
        </div>

        <FloatingInput v-model="student.email" @focusout="validateField('email', student)" @change="validateField('email', student)"  :error="errors.email" :success="success.email" :label="`${label('site.email_address')} *`"  />
        <FloatingInput type="tel" v-model="student.phone" @focusout="validateField('phone', student)" @change="validateField('phone', student)"  :error="errors.phone" :success="success.phone" :label="`${label('site.phone_number')} *`"  />
    </div>
</template>

